import {
  CRON_PRESETS,
  isValidTimeZone,
  nextCronTimes,
  type WorkflowDto,
  type WorkflowScheduleDto,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarClock, Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { ErrorAlert } from '@/components/error-alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDateTime, formatRelative } from '@/lib/format';

const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const zones: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf(
      'timeZone',
    );
  } catch {
    return ['UTC'];
  }
})();

function ScheduleDialog({
  tenantId,
  workflow,
  schedule,
  open,
  onOpenChange,
}: {
  tenantId: string;
  workflow: WorkflowDto;
  schedule: WorkflowScheduleDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(schedule?.name ?? '');
  const [cron, setCron] = useState(schedule?.cron ?? '0 9 * * 1-5');
  const [timezone, setTimezone] = useState(schedule?.timezone ?? browserZone);
  const [input, setInput] = useState(() =>
    JSON.stringify(
      schedule?.input ??
        Object.fromEntries(
          workflow.definition.inputs.map((i) => [i.name, i.type === 'number' ? 0 : '']),
        ),
      null,
      2,
    ),
  );
  const preview = useMemo(() => {
    if (!isValidTimeZone(timezone)) return { error: 'Unknown time zone' };
    try {
      return { times: nextCronTimes(cron, new Date(), timezone, 3) };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [cron, timezone]);
  const parsedInput = useMemo(() => {
    try {
      const v = JSON.parse(input || '{}') as unknown;
      return v && typeof v === 'object' && !Array.isArray(v)
        ? (v as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }, [input]);

  const save = useMutation({
    mutationFn: () =>
      schedule
        ? api<WorkflowScheduleDto>(`/workflows/${workflow.id}/schedules/${schedule.id}`, {
            method: 'PATCH',
            body: { name, cron, timezone, input: parsedInput ?? {} },
          })
        : api<WorkflowScheduleDto>(`/workflows/${workflow.id}/schedules`, {
            method: 'POST',
            body: { name, cron, timezone, input: parsedInput ?? {} },
          }),
    onSuccess: () => {
      toast.success(schedule ? 'Schedule saved' : 'Schedule created');
      void queryClient.invalidateQueries({
        queryKey: ['workflow-schedules', tenantId, workflow.id],
      });
      onOpenChange(false);
    },
  });
  const fieldError = (path: string) =>
    save.error instanceof ApiError ? save.error.fieldError(path) : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{schedule ? 'Edit schedule' : 'New schedule'}</DialogTitle>
          <DialogDescription>
            Runs {workflow.name} automatically. Each occurrence runs once, even with several
            servers.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <ErrorAlert error={save.error} />
          <div className="grid gap-1.5">
            <Label htmlFor="sch-name">Name</Label>
            <Input
              id="sch-name"
              value={name}
              placeholder="Weekday morning"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sch-preset">Repeat</Label>
            <Select
              value={CRON_PRESETS.find((p) => p.cron === cron)?.cron ?? 'custom'}
              onValueChange={(v) => v !== 'custom' && setCron(v)}
            >
              <SelectTrigger id="sch-preset" aria-label="Repeat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CRON_PRESETS.map((p) => (
                  <SelectItem key={p.cron} value={p.cron}>
                    {p.label}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom cron expression</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1.5">
              <Label htmlFor="sch-cron">Cron</Label>
              <Input
                id="sch-cron"
                className="font-mono"
                value={cron}
                aria-invalid={Boolean(preview.error) || Boolean(fieldError('cron'))}
                onChange={(e) => setCron(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="sch-tz">Time zone</Label>
              <Input
                id="sch-tz"
                list="sch-tz-list"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
              <datalist id="sch-tz-list">
                {zones.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
            </div>
          </div>
          <div className="text-xs" aria-live="polite">
            {preview.error ? (
              <p className="text-destructive">{fieldError('cron') ?? preview.error}</p>
            ) : (
              <div className="text-muted-foreground grid gap-0.5">
                <span>Next runs:</span>
                {preview.times!.map((t) => (
                  <span key={t.toISOString()} className="font-mono">
                    {t.toLocaleString([], {
                      timeZone: timezone,
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}{' '}
                    ({timezone})
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="sch-input">Input (JSON)</Label>
            <Textarea
              id="sch-input"
              className="min-h-24 font-mono text-xs"
              value={input}
              aria-invalid={parsedInput === null}
              onChange={(e) => setInput(e.target.value)}
            />
            {parsedInput === null && <p className="text-destructive text-xs">Use a JSON object.</p>}
            {save.error instanceof ApiError &&
              save.error.errors
                .filter((e) => e.path.startsWith('input.'))
                .map((e) => (
                  <p key={e.path} className="text-destructive text-xs">
                    <span className="font-mono">{e.path}</span>: {e.message}
                  </p>
                ))}
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={save.isPending || Boolean(preview.error) || parsedInput === null}
            >
              {save.isPending ? 'Saving…' : schedule ? 'Save schedule' : 'Create schedule'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function WorkflowSchedules({
  tenantId,
  workflow,
  canEdit,
}: {
  tenantId: string;
  workflow: WorkflowDto;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const schedules = useQuery({
    queryKey: ['workflow-schedules', tenantId, workflow.id],
    queryFn: () => api<{ items: WorkflowScheduleDto[] }>(`/workflows/${workflow.id}/schedules`),
    refetchInterval: 30_000,
  });
  const [editing, setEditing] = useState<WorkflowScheduleDto | 'new' | null>(null);
  const [deleting, setDeleting] = useState<WorkflowScheduleDto | null>(null);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['workflow-schedules', tenantId, workflow.id] });
  const toggle = useMutation({
    mutationFn: (s: WorkflowScheduleDto) =>
      api(`/workflows/${workflow.id}/schedules/${s.id}`, {
        method: 'PATCH',
        body: { enabled: !s.enabled },
      }),
    onSuccess: invalidate,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: (s: WorkflowScheduleDto) =>
      api(`/workflows/${workflow.id}/schedules/${s.id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const rows = schedules.data?.items ?? [];
  return (
    <div className="grid gap-3">
      <ErrorAlert error={schedules.error} />
      {schedules.isPending ? (
        <Skeleton className="h-24" />
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No schedules. Add one to run this workflow on a cron schedule.
        </p>
      ) : (
        <ul className="grid gap-2">
          {rows.map((s) => (
            <li key={s.id} className="grid gap-1.5 rounded-md border p-2 text-xs">
              <div className="flex items-center gap-2">
                <CalendarClock className="size-4" aria-hidden />
                <span className="text-sm font-medium">{s.name || s.cron}</span>
                <Badge variant={s.enabled ? 'secondary' : 'outline'}>
                  {s.enabled ? 'On' : 'Paused'}
                </Badge>
                {canEdit && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="ml-auto size-7"
                    aria-label={`Edit ${s.name || s.cron}`}
                    onClick={() => setEditing(s)}
                  >
                    <Pencil />
                  </Button>
                )}
              </div>
              <span className="text-muted-foreground font-mono">
                {s.cron} · {s.timezone}
              </span>
              <span className="text-muted-foreground">
                {s.nextRunAt ? `Next ${formatDateTime(s.nextRunAt)}` : 'Not scheduled'}
                {s.lastRunAt && ` · last ${formatRelative(s.lastRunAt)}`}
                {s.lastRunId && (
                  <>
                    {' · '}
                    <Link
                      to="/traces/workflows/$runId"
                      params={{ runId: s.lastRunId }}
                      className="underline"
                    >
                      last run
                    </Link>
                  </>
                )}
              </span>
              {s.lastError && <span className="text-destructive">{s.lastError}</span>}
              {canEdit && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => toggle.mutate(s)}>
                    {s.enabled ? 'Pause' : 'Resume'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setDeleting(s)}>
                    Delete
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <Button
          variant="outline"
          size="sm"
          className="justify-self-start"
          onClick={() => setEditing('new')}
        >
          <Plus aria-hidden /> Add schedule
        </Button>
      )}
      {editing && (
        <ScheduleDialog
          key={editing === 'new' ? 'new' : editing.id}
          tenantId={tenantId}
          workflow={workflow}
          schedule={editing === 'new' ? null : editing}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
      <ConfirmDeleteDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete ${deleting?.name || deleting?.cron || 'schedule'}?`}
        description="The schedule stops. Runs it already started are kept."
        onConfirm={() => remove.mutateAsync(deleting!)}
      />
    </div>
  );
}
